import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { Send, Plus, X, MessageSquare, Pin, PinOff, Trash2, ChevronDown } from 'lucide-react';

interface ChatMessage {
  id: number;
  facility_id: number;
  sender_id: number;
  sender_name: string;
  message_type: 'text' | 'approval_request';
  content: string;
  meta: ApprovalMeta | null;
  read_by: number[];
  recipient_id: number | null;
  created_at: string;
}

interface ApprovalMeta {
  requestId: number;
  requestType: 'password' | 'email';
  targetUserId: number;
  targetUserName: string;
  newEmail: string | null;
  status: 'pending' | 'approved' | 'rejected';
  grantToken?: string;
}

interface ConversationUser {
  other_id: number;
  first_name: string | null;
  last_name: string | null;
  username: string;
  last_msg: string;
  last_at: string;
}

interface FacilityUser {
  id: number;
  first_name: string | null;
  last_name: string | null;
  username: string;
  role: string;
  facility_id?: number;
  facility_name?: string;
  department_id?: number | null;
  department_name?: string | null;
  department_color?: string | null;
}

interface ChatProps {
  isOpen: boolean;
  onClose: () => void;
  facilityName?: string;
  onUnreadCountChange?: (count: number) => void;
}

type Tab = 'main' | 'org' | 'department' | 'dm';

const TAB_BG_ACTIVE = '#131720';
const TAB_BG_INACTIVE = '#0a0d14';
const PANEL_BORDER = '#1a2035';

function relativeTime(iso: string) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
}

const USER_COLORS = [
  '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
  '#ec4899','#06b6d4','#84cc16','#f97316','#14b8a6',
  '#6366f1','#a855f7','#e11d48','#0ea5e9','#22c55e',
  '#eab308','#f43f5e','#7c3aed','#0891b2','#65a30d',
];
const getUserColor = (userId: number) => USER_COLORS[userId % USER_COLORS.length];

const BADGE_STYLE: React.CSSProperties = {
  background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700,
  borderRadius: 999, padding: '1px 6px', minWidth: 18, textAlign: 'center',
  display: 'inline-block', marginLeft: 6, lineHeight: '16px',
};
const deptBadge = (color: string, _name: string) => ({
  fontSize: 10, padding: '1px 6px', borderRadius: 20,
  background: color + '22', border: `1px solid ${color}66`, color,
  whiteSpace: 'nowrap' as const, display: 'inline-block',
});

function avatarInitial(name: string) {
  return name[0]?.toUpperCase() ?? '?';
}

function avatarInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function dName(u: { first_name: string | null; last_name: string | null; username: string }) {
  return u.first_name ? `${u.first_name} ${u.last_name ?? ''}`.trim() : u.username;
}

// ─── Channel tab bar ──────────────────────────────────────────────────────────
function TabBar({ active, onChange, mainUnread, dmUnread, showOrg, showDept, deptName, deptColor }: {
  active: Tab; onChange: (t: Tab) => void; mainUnread: number; dmUnread: number;
  showOrg: boolean; showDept: boolean; deptName?: string | null; deptColor?: string | null;
}) {
  const pillBase: React.CSSProperties = {
    borderRadius: 7, padding: '5px 11px', fontSize: 11, fontWeight: 500,
    cursor: 'pointer', border: 'none', transition: 'all 0.15s',
    whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4,
    background: 'transparent', color: '#9ca3af',
  };
  const pillOn: React.CSSProperties = { background: 'rgba(255,255,255,0.15)', color: '#f9fafb', fontWeight: 600 };
  return (
    <div style={{ padding: '8px 14px 0', background: TAB_BG_INACTIVE, borderBottom: `1px solid ${PANEL_BORDER}` }}>
      <div style={{ border: '1px solid rgba(255,255,255,0.18)', borderRadius: 10, display: 'inline-flex', padding: 3, background: 'rgba(255,255,255,0.03)', gap: 2, marginBottom: 8, flexWrap: 'wrap' }}>
        <button style={{ ...pillBase, ...(active === 'main' ? pillOn : {}) }} onClick={() => onChange('main')}>
          🏢 Facility{mainUnread > 0 && active !== 'main' && <span style={BADGE_STYLE}>{mainUnread}</span>}
        </button>
        {showOrg && (
          <button style={{ ...pillBase, ...(active === 'org' ? pillOn : {}) }} onClick={() => onChange('org')}>
            🌐 Org-Wide
          </button>
        )}
        {showDept && deptName && (
          <button style={{ ...pillBase, ...(active === 'department' ? pillOn : {}) }} onClick={() => onChange('department')}>
            {deptColor && <span style={{ width: 7, height: 7, borderRadius: '50%', background: deptColor, flexShrink: 0 }} />}
            {deptName}
          </button>
        )}
        <button style={{ ...pillBase, ...(active === 'dm' ? pillOn : {}) }} onClick={() => onChange('dm')}>
          ✉ DMs{dmUnread > 0 && active !== 'dm' && <span style={BADGE_STYLE}>{dmUnread}</span>}
        </button>
      </div>
    </div>
  );
}

// ─── Chat bubble ──────────────────────────────────────────────────────────────
function ChatBubble({ msg }: { msg: ChatMessage; currentUserId: number }) {
  const color = getUserColor(msg.sender_id);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: color + '33', border: `1px solid ${color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color }}>
        {avatarInitials(msg.sender_name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color }}>{msg.sender_name}</span>
          <span style={{ fontSize: 10, color: '#475569' }}>{relativeTime(msg.created_at)}</span>
        </div>
        <div style={{ padding: '8px 12px', background: '#1e293b', borderRadius: 8, borderLeft: `3px solid ${color}`, fontSize: 13, color: '#e2e8f0', lineHeight: 1.5, wordBreak: 'break-word' }}>
          {msg.content}
        </div>
      </div>
    </div>
  );
}

// ─── Approval request card ────────────────────────────────────────────────────
function ApprovalRequestCard({ msg, currentUser, onResolve, onSetPassword }: {
  msg: ChatMessage;
  currentUser: { id: number; role: string };
  onResolve: (requestId: number, status: 'approved' | 'rejected') => Promise<void>;
  onSetPassword: (targetUserId: number, grantToken: string) => void;
}) {
  const meta = msg.meta as ApprovalMeta;
  if (!meta) return null;
  const isAdmin = currentUser.role === 'admin';
  const isOrgAdmin = currentUser.role === 'org_admin';
  const isSender = msg.sender_id === currentUser.id;
  const isPending = meta.status === 'pending';
  const statusColor = { pending: '#f59e0b', approved: '#4ade80', rejected: '#f87171' }[meta.status] ?? '#f59e0b';
  return (
    <div style={{ background: '#0f1521', border: `1px solid ${isPending ? '#f59e0b44' : statusColor + '33'}`, borderRadius: 10, padding: '12px 14px', maxWidth: '95%', alignSelf: 'stretch' }}>
      <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, marginBottom: 6 }}>
        🔐 {meta.requestType === 'password' ? 'Password' : 'Email'} Change Request
      </div>
      <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>
        <strong style={{ color: '#e2e8f0' }}>{msg.sender_name}</strong> is requesting to{' '}
        {meta.requestType === 'password' ? 'reset the password' : `change the email to ${meta.newEmail}`}{' '}
        for <strong style={{ color: '#e2e8f0' }}>{meta.targetUserName}</strong>
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: statusColor + '15', border: `1px solid ${statusColor}33`, borderRadius: 6, padding: '3px 10px', fontSize: 11, color: statusColor, marginBottom: (isPending && isAdmin) ? 10 : 0 }}>
        {meta.status === 'pending' ? '⏳ Awaiting approval' : meta.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
      </div>
      {isPending && isAdmin && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button onClick={() => onResolve(meta.requestId, 'approved')} style={{ flex: 1, padding: '7px 0', background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)', color: '#4ade80', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>✓ Approve</button>
          <button onClick={() => onResolve(meta.requestId, 'rejected')} style={{ flex: 1, padding: '7px 0', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>✗ Reject</button>
        </div>
      )}
      {meta.status === 'approved' && meta.requestType === 'password' && (isSender || isOrgAdmin) && meta.grantToken && (
        <button onClick={() => onSetPassword(meta.targetUserId, meta.grantToken!)} style={{ marginTop: 10, width: '100%', padding: '7px 0', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', color: '#818cf8', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>🔑 Set New Password</button>
      )}
    </div>
  );
}

// ─── Send button (Mockup 3 style: round blue circle, white Send icon) ────────
function SendBtn({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ width: 38, height: 38, borderRadius: '50%', background: disabled ? '#1a2035' : '#3b82f6', border: 'none', color: disabled ? '#475569' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'default' : 'pointer', flexShrink: 0 }}>
      <Send size={16} />
    </button>
  );
}

// ─── Message input bar ────────────────────────────────────────────────────────
function MsgInput({ value, onChange, onSend, placeholder, inputRef }: {
  value: string; onChange: (v: string) => void; onSend: () => void;
  placeholder?: string; inputRef?: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div style={{ padding: '10px 12px', borderTop: `1px solid ${PANEL_BORDER}`, display: 'flex', gap: 8, background: TAB_BG_ACTIVE }}>
      <input
        ref={inputRef} value={value} onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
        placeholder={placeholder ?? 'Message your team...'}
        style={{ flex: 1, padding: '10px 16px', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 999, color: '#e2e8f0', fontSize: 13, outline: 'none' }}
      />
      <SendBtn onClick={onSend} disabled={!value.trim()} />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Chat({ isOpen, onClose, facilityName, onUnreadCountChange }: ChatProps) {
  const { user: currentUser } = useAuth();
  const uid = currentUser?.id ?? 0;
  const userRole = currentUser?.role ?? 'viewer';
  const userDeptId = (currentUser as any)?.departmentId as number | null | undefined;
  const userDeptName = (currentUser as any)?.departmentName as string | null | undefined;
  const userDeptColor = (currentUser as any)?.departmentColor as string | null | undefined;
  const showOrg = userRole === 'org_admin' || userRole === 'admin';
  const showDept = !!userDeptId;

  const [activeTab, setActiveTab] = useState<Tab>('main');

  // Main chat
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const mainEndRef = useRef<HTMLDivElement>(null);
  const mainInputRef = useRef<HTMLInputElement>(null);
  const lastSentMainRef = useRef<number>(0);
  const lastSentDmRef = useRef<number>(0);

  // DM
  const [conversations, setConversations] = useState<ConversationUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<{ id: number; name: string } | null>(null);
  const [dmMessages, setDmMessages] = useState<ChatMessage[]>([]);
  const [dmDraft, setDmDraft] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [facilityUsers, setFacilityUsers] = useState<FacilityUser[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState('');
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerFacilityFilter, setPickerFacilityFilter] = useState<string>('all');
  const [pickerDropOpen, setPickerDropOpen] = useState(false);
  const [pickerFacilities, setPickerFacilities] = useState<string[]>([]);
  const pickerDropRef = useRef<HTMLDivElement>(null);
  const dmEndRef = useRef<HTMLDivElement>(null);
  const dmInputRef = useRef<HTMLInputElement>(null);

  // Unread counts
  const [unreadCounts, setUnreadCounts] = useState<{ main: number; dms: Record<string, number> }>({ main: 0, dms: {} });

  // Pin / click-outside
  const panelRef = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(() => localStorage.getItem('chat_pinned') === 'true');

  // Grant modal
  const [grantModal, setGrantModal] = useState<{ targetUserId: number; grantToken: string } | null>(null);
  const [grantPw, setGrantPw] = useState('');
  const [grantConfirm, setGrantConfirm] = useState('');
  const [grantErr, setGrantErr] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);

  const token = () => localStorage.getItem('accessToken') ?? '';
  // Include X-Facility-ID header so facility-switched admins see the correct facility's chat
  const facilityH = (): Record<string, string> => {
    const fid = localStorage.getItem('selectedFacilityId');
    return fid ? { 'X-Facility-ID': fid } : {};
  };
  const authH = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json', ...facilityH() } as Record<string, string>);

  // Scroll lock
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Click-outside: close panel when not pinned
  useEffect(() => {
    if (!isOpen || pinned) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, pinned, onClose]);

  function togglePin() {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem('chat_pinned', String(next));
  }

  // Listen for openDirectMessage event from Team Directory
  useEffect(() => {
    const handler = (e: Event) => {
      const { userId, userName } = (e as CustomEvent<{ userId: number; userName: string }>).detail;
      setActiveTab('dm');
      setSelectedUser({ id: userId, name: userName });
    };
    window.addEventListener('openDirectMessage', handler);
    return () => window.removeEventListener('openDirectMessage', handler);
  }, []);

  // ── Main/channel chat ─────────────────────────────────────────────────────────
  const fetchMain = useCallback(async () => {
    // Skip poll cycle immediately after a send to avoid overwriting optimistic state
    if (Date.now() - lastSentMainRef.current < 3000) return;
    try {
      let url = '/api/chat/messages?limit=50';
      if (activeTab === 'org') url += '&channel=org';
      else if (activeTab === 'department' && userDeptId) url += `&channel=department&departmentId=${userDeptId}`;
      else url += '&channel=facility';
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token()}`, ...facilityH() } });
      const d = await r.json();
      if (d.success) setMessages(d.data ?? []);
    } catch { /* ignore */ }
  }, [activeTab, userDeptId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || activeTab === 'dm') return;
    setMessages([]); // clear stale channel messages on switch
    fetchMain();
    const id = setInterval(fetchMain, 10_000);
    return () => clearInterval(id);
  }, [isOpen, activeTab, fetchMain]);

  useEffect(() => {
    if (isOpen && activeTab !== 'dm')
      setTimeout(() => mainEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [messages, isOpen, activeTab]);

  useEffect(() => {
    if (isOpen && activeTab !== 'dm') setTimeout(() => mainInputRef.current?.focus(), 120);
  }, [isOpen, activeTab]);

  // ── DM conversations ──────────────────────────────────────────────────────────
  const fetchConvos = useCallback(async () => {
    try {
      const r = await fetch('/api/chat/conversations', { headers: { Authorization: `Bearer ${token()}`, ...facilityH() } });
      const d = await r.json();
      if (d.success) setConversations(d.data ?? []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!isOpen || activeTab !== 'dm') return;
    fetchConvos();
    const id = setInterval(fetchConvos, 10_000);
    return () => clearInterval(id);
  }, [isOpen, activeTab, fetchConvos]);

  // Auto-select the single unread DM conversation when switching to the DM tab
  useEffect(() => {
    if (activeTab !== 'dm' || selectedUser !== null || conversations.length === 0) return;
    const unreadConvos = conversations.filter(c => (unreadCounts.dms[String(c.other_id)] ?? 0) > 0);
    if (unreadConvos.length === 1) {
      const c = unreadConvos[0];
      const name = c.first_name ? `${c.first_name} ${c.last_name ?? ''}`.trim() : c.username;
      setSelectedUser({ id: c.other_id, name });
      setDmMessages([]);
    }
  }, [activeTab, selectedUser, conversations, unreadCounts.dms]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── DM thread ─────────────────────────────────────────────────────────────────
  const fetchDm = useCallback(async (rid: number) => {
    // Skip poll cycle immediately after a send to avoid overwriting optimistic state
    if (Date.now() - lastSentDmRef.current < 3000) return;
    try {
      console.log('[chat] fetching DMs for recipient:', rid);
      const r = await fetch(`/api/chat/messages?limit=50&recipientId=${rid}`, { headers: { Authorization: `Bearer ${token()}`, ...facilityH() } });
      const d = await r.json();
      if (d.success) setDmMessages(d.data ?? []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!isOpen || activeTab !== 'dm' || !selectedUser) return;
    fetchDm(selectedUser.id);
    const id = setInterval(() => fetchDm(selectedUser.id), 8_000);
    return () => clearInterval(id);
  }, [isOpen, activeTab, selectedUser, fetchDm]);

  useEffect(() => {
    if (isOpen && activeTab === 'dm' && selectedUser)
      setTimeout(() => dmEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [dmMessages, isOpen, activeTab, selectedUser]);

  useEffect(() => {
    if (isOpen && activeTab === 'dm' && selectedUser) setTimeout(() => dmInputRef.current?.focus(), 120);
  }, [isOpen, activeTab, selectedUser]);

  // ── Unread counts ─────────────────────────────────────────────────────────────
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const r = await fetch('/api/chat/unread-counts', { headers: { Authorization: `Bearer ${token()}`, ...facilityH() } });
      const d = await r.json();
      if (d.success && d.data) {
        setUnreadCounts(prev => ({
          main: isOpen && activeTab === 'main' ? 0 : Math.max(prev.main, d.data.main ?? 0),
          dms: Object.fromEntries(
            Object.entries(d.data.dms ?? {}).map(([id, count]) =>
              [id, selectedUser?.id === Number(id) ? 0 : Math.max(prev.dms[id] ?? 0, count as number)]
            )
          ),
        }));
      }
    } catch { /* ignore */ }
  }, [isOpen, activeTab, selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll unread counts always (for TopBar badge even when panel is closed)
  useEffect(() => {
    fetchUnreadCounts();
    const id = setInterval(fetchUnreadCounts, 30_000);
    return () => clearInterval(id);
  }, [fetchUnreadCounts]);

  // Notify parent (TopBar) of total unread count
  useEffect(() => {
    const total = unreadCounts.main + Object.values(unreadCounts.dms).reduce((a, b) => a + b, 0);
    onUnreadCountChange?.(total);
  }, [unreadCounts, onUnreadCountChange]);

  const markRead = useCallback(async (type: 'main' | 'dm', senderId?: number) => {
    // Optimistically clear badge immediately
    if (type === 'main') {
      setUnreadCounts(prev => ({ ...prev, main: 0 }));
    } else if (type === 'dm' && senderId) {
      setUnreadCounts(prev => ({ ...prev, dms: { ...prev.dms, [String(senderId)]: 0 } }));
    }
    try {
      await fetch('/api/chat/mark-read', { method: 'POST', headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ type, senderId }) });
      // After server confirms, refresh all counts so other tabs show the correct state
      await fetchUnreadCounts();
    } catch { /* ignore */ }
  }, [fetchUnreadCounts]);

  useEffect(() => {
    if (!isOpen) return;
    if (activeTab !== 'dm') markRead('main');
    else if (activeTab === 'dm' && selectedUser) markRead('dm', selectedUser.id);
  }, [isOpen, activeTab, selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Facility users for picker ─────────────────────────────────────────────────
  useEffect(() => {
    if (!showPicker) return;
    setPickerLoading(true);
    setPickerError('');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => { controller.abort(); }, 5000);
    const facilityHeaders = { Authorization: `Bearer ${token()}`, ...facilityH() };
    // Users fetch omits X-Facility-ID so the server returns all org users (not facility-scoped)
    const usersHeaders = { Authorization: `Bearer ${token()}` };
    Promise.all([
      fetch('/api/users', { headers: usersHeaders, signal: controller.signal }).then((r) => r.json()),
      fetch('/api/facilities', { headers: facilityHeaders, signal: controller.signal }).then((r) => r.json()),
    ])
      .then(([usersData, facilitiesData]) => {
        if (usersData.success && usersData.data) {
          setFacilityUsers(usersData.data);
        } else {
          setPickerError('Failed to load users');
        }
        if (facilitiesData.success && facilitiesData.data) {
          const names: string[] = facilitiesData.data.map((f: { name: string }) => f.name).filter(Boolean);
          setPickerFacilities(names);
        }
      })
      .catch((e) => {
        if (e.name !== 'AbortError') setPickerError('Failed to load users');
        else setPickerError('Request timed out');
      })
      .finally(() => { setPickerLoading(false); clearTimeout(timeoutId); });
    return () => { controller.abort(); clearTimeout(timeoutId); };
  }, [showPicker]);

  // ── Actions ───────────────────────────────────────────────────────────────────
  const sendMain = async () => {
    if (!draft.trim()) return;
    const text = draft.trim(); setDraft('');
    const body: Record<string, unknown> = { content: text, channel: 'facility' };
    if (activeTab === 'org') body.channel = 'org';
    else if (activeTab === 'department' && userDeptId) { body.channel = 'department'; body.departmentId = userDeptId; }
    try {
      const r = await fetch('/api/chat/messages', { method: 'POST', headers: authH(), body: JSON.stringify(body) });
      const d = await r.json();
      if (d.success) {
        lastSentMainRef.current = Date.now();
        setMessages((p) => [...p, d.data]);
      } else { setDraft(text); }
    } catch { setDraft(text); }
  };

  const sendDm = async () => {
    if (!dmDraft.trim() || !selectedUser) return;
    const text = dmDraft.trim(); setDmDraft('');
    const rid = selectedUser.id;
    try {
      const r = await fetch('/api/chat/messages', { method: 'POST', headers: authH(), body: JSON.stringify({ content: text, recipientId: rid }) });
      const d = await r.json();
      if (d.success) {
        lastSentDmRef.current = Date.now();
        setDmMessages((p) => [...p, d.data]);
        fetchConvos();
      } else { setDmDraft(text); }
    } catch { setDmDraft(text); }
  };

  const handleResolve = async (requestId: number, status: 'approved' | 'rejected') => {
    try {
      const r = await fetch(`/api/chat/approval-request/${requestId}/resolve`, { method: 'PATCH', headers: authH(), body: JSON.stringify({ status }) });
      const d = await r.json();
      if (d.success) fetchMain();
    } catch { /* ignore */ }
  };

  const openGrant = (targetUserId: number, grantToken: string) => {
    setGrantModal({ targetUserId, grantToken }); setGrantPw(''); setGrantConfirm(''); setGrantErr('');
  };

  const submitGrant = async () => {
    if (!grantModal) return;
    if (grantPw.length < 8) { setGrantErr('Password must be at least 8 characters'); return; }
    if (grantPw !== grantConfirm) { setGrantErr('Passwords do not match'); return; }
    setGrantLoading(true);
    try {
      const r = await fetch(`/api/users/${grantModal.targetUserId}/password-by-grant`, { method: 'PUT', headers: authH(), body: JSON.stringify({ newPassword: grantPw, grantToken: grantModal.grantToken }) });
      const d = await r.json();
      if (d.success) { setGrantModal(null); fetchMain(); }
      else setGrantErr(d.error ?? 'Failed to set password');
    } catch { setGrantErr('Network error'); }
    finally { setGrantLoading(false); }
  };

  const pickUser = (u: FacilityUser) => {
    const name = dName(u);
    setSelectedUser({ id: u.id, name });
    setDmMessages([]);
    setShowPicker(false);
  };

  const openPicker = () => {
    setFacilityUsers([]);
    setPickerFacilities([]);
    setPickerError('');
    setPickerLoading(true);
    setPickerSearch('');
    setPickerFacilityFilter('all');
    setPickerDropOpen(false);
    setShowPicker(true);
  };

  // Close facility dropdown on outside click
  useEffect(() => {
    if (!pickerDropOpen) return;
    const handler = (e: MouseEvent) => {
      if (pickerDropRef.current && !pickerDropRef.current.contains(e.target as Node)) {
        setPickerDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerDropOpen]);

  const deleteDmConversation = async () => {
    if (!selectedUser) return;
    try {
      await fetch(`/api/chat/conversations/${selectedUser.id}`, { method: 'DELETE', headers: authH() });
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      setDmMessages([]);
      fetchConvos();
    } catch { /* ignore */ }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  const panelWidth = 560;

  return (
    <>
      {/* ── Chat panel ─────────────────────────────────────────────────────────── */}
      <div ref={panelRef} style={{
        position: 'fixed', top: 0, right: 0, height: '100vh',
        width: panelWidth, background: TAB_BG_ACTIVE,
        borderLeft: `1px solid ${PANEL_BORDER}`,
        display: 'flex', flexDirection: 'column', zIndex: 200,
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease, width 0.2s ease',
      }}>
        {/* Header + tabs */}
        <div style={{ background: TAB_BG_INACTIVE, padding: '12px 14px 0', borderBottom: `1px solid ${PANEL_BORDER}` }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, color: '#e2e8f0', flex: 1, fontSize: 14 }}>
              💬 Team Chat
              {facilityName && <span style={{ marginLeft: 8, fontSize: 11, color: '#475569', fontWeight: 400 }}>{facilityName}</span>}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0 2px' }}>
              <X size={16} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <TabBar
              active={activeTab}
              onChange={setActiveTab}
              mainUnread={unreadCounts.main}
              dmUnread={Object.values(unreadCounts.dms).reduce((a, b) => a + b, 0)}
              showOrg={showOrg}
              showDept={showDept}
              deptName={userDeptName}
              deptColor={userDeptColor}
            />
            <button
              onClick={togglePin}
              title={pinned ? 'Unpin (click outside will close)' : 'Pin (click outside will not close)'}
              style={{
                background: pinned ? 'rgba(99,102,241,0.15)' : 'none',
                border: pinned ? '1px solid rgba(99,102,241,0.3)' : '1px solid transparent',
                borderRadius: 6, color: pinned ? '#818cf8' : '#475569',
                cursor: 'pointer', padding: '4px 7px', marginBottom: 2,
                display: 'flex', alignItems: 'center',
              }}
            >
              {pinned ? <PinOff size={13} /> : <Pin size={13} />}
            </button>
          </div>
        </div>

        {/* ── Broadcast channels (Facility / Org-Wide / Department) ─────────────── */}
        {(activeTab === 'main' || activeTab === 'org' || activeTab === 'department') && (
          <>
            {/* Channel header for org/dept */}
            {activeTab === 'org' && (
              <div style={{ padding: '6px 14px', borderBottom: `1px solid ${PANEL_BORDER}`, display: 'flex', alignItems: 'center', gap: 6, background: TAB_BG_ACTIVE, flexShrink: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>🌐 Org-Wide</span>
                <span style={{ fontSize: 11, color: '#475569' }}>All team members</span>
              </div>
            )}
            {activeTab === 'department' && userDeptName && (
              <div style={{ padding: '6px 14px', borderBottom: `1px solid ${PANEL_BORDER}`, display: 'flex', alignItems: 'center', gap: 6, background: TAB_BG_ACTIVE, flexShrink: 0 }}>
                {userDeptColor && <span style={{ width: 8, height: 8, borderRadius: '50%', background: userDeptColor, flexShrink: 0 }} />}
                <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>{userDeptName}</span>
                <span style={{ fontSize: 11, color: '#475569' }}>Department channel</span>
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {messages.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#374151', fontSize: 13 }}>No messages yet. Say hello! 👋</div>
              )}
              {messages.map((msg) =>
                msg.message_type === 'approval_request'
                  ? <ApprovalRequestCard key={msg.id} msg={msg} currentUser={{ id: uid, role: currentUser?.role ?? 'viewer' }} onResolve={handleResolve} onSetPassword={openGrant} />
                  : <ChatBubble key={msg.id} msg={msg} currentUserId={uid} />
              )}
              <div ref={mainEndRef} />
            </div>
            <MsgInput value={draft} onChange={setDraft} onSend={sendMain} placeholder="Message your team..." inputRef={mainInputRef} />
          </>
        )}

        {/* ── Direct Messages ────────────────────────────────────────────────────── */}
        {activeTab === 'dm' && (
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Left: conversation list */}
            <div style={{ width: 200, flexShrink: 0, borderRight: `1px solid ${PANEL_BORDER}`, display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '8px 10px' }}>
                <button
                  onClick={openPicker}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 8px', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 6, color: '#60a5fa', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  <Plus size={12} /> New Message
                </button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {conversations.length === 0 && (
                  <div style={{ padding: '16px 10px', color: '#374151', fontSize: 12, textAlign: 'center' }}>No conversations yet</div>
                )}
                {conversations.map((c) => {
                  const name = c.first_name ? `${c.first_name} ${c.last_name ?? ''}`.trim() : c.username;
                  const isSelected = selectedUser?.id === c.other_id;
                  return (
                    <button
                      key={c.other_id}
                      onClick={() => {
                        setSelectedUser({ id: c.other_id, name });
                        setDmMessages([]);
                        // Immediately clear the badge; markRead server call fires via selectedUser useEffect
                        setUnreadCounts(prev => ({ ...prev, dms: { ...prev.dms, [String(c.other_id)]: 0 } }));
                      }}
                      style={{ width: '100%', textAlign: 'left', padding: '9px 10px', background: isSelected ? 'rgba(59,130,246,0.1)' : 'transparent', borderLeft: `3px solid ${isSelected ? '#3b82f6' : 'transparent'}`, border: 'none', borderTop: `1px solid ${PANEL_BORDER}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}
                    >
                      <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: getUserColor(c.other_id) + '33', border: `1px solid ${getUserColor(c.other_id)}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: getUserColor(c.other_id) }}>
                        {avatarInitials(name)}
                      </div>
                      <div style={{ overflow: 'hidden', flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                          {!isSelected && (unreadCounts.dms[String(c.other_id)] ?? 0) > 0 && (
                            <span style={{ ...BADGE_STYLE, marginLeft: 4, flexShrink: 0 }}>{unreadCounts.dms[String(c.other_id)]}</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.last_msg}</div>
                        {(() => { const fu = facilityUsers.find(u => u.id === c.other_id); return fu?.department_name ? <div style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>{fu.department_name}</div> : null; })()}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: thread */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {!selectedUser ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#374151', gap: 10 }}>
                  <MessageSquare size={30} color="#1e293b" />
                  <span style={{ fontSize: 13 }}>Select a team member to message</span>
                </div>
              ) : (
                <>
                  <div style={{ padding: '9px 14px', borderBottom: `1px solid ${PANEL_BORDER}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: getUserColor(selectedUser.id) + '33', border: `1px solid ${getUserColor(selectedUser.id)}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: getUserColor(selectedUser.id), flexShrink: 0 }}>
                      {avatarInitials(selectedUser.name)}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', flex: 1 }}>{selectedUser.name}</span>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      title="Delete conversation"
                      style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: '2px 4px', display: 'flex', alignItems: 'center' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {dmMessages.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '30px 0', color: '#374151', fontSize: 13 }}>No messages yet. Start the conversation!</div>
                    )}
                    {dmMessages.map((msg) => <ChatBubble key={msg.id} msg={msg} currentUserId={uid} />)}
                    <div ref={dmEndRef} />
                  </div>
                  <MsgInput value={dmDraft} onChange={setDmDraft} onSend={sendDm} placeholder={`Message ${selectedUser.name}...`} inputRef={dmInputRef} />
                </>
              )}
            </div>
          </div>
        )}

      {/* ── User picker — avatar card grid, inside panel ──────────────────────────── */}
      {showPicker && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }} onClick={() => setShowPicker(false)}>
          <div style={{ background: '#0c0f1a', border: `1px solid ${PANEL_BORDER}`, borderRadius: 10, width: 460, maxHeight: 520, display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${PANEL_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 13 }}>New Direct Message</span>
              <button onClick={() => setShowPicker(false)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={15} /></button>
            </div>
            {/* Search + facility filter row */}
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${PANEL_BORDER}`, display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <input
                autoFocus
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Search team members…"
                style={{ flex: 1, padding: '7px 12px', background: '#0f172a', border: `1px solid ${PANEL_BORDER}`, borderRadius: 6, color: '#e2e8f0', fontSize: 13, outline: 'none' }}
              />
              {/* Custom dark facility filter dropdown */}
              <div ref={pickerDropRef} style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  onClick={() => setPickerDropOpen((v) => !v)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 10px', background: '#0f172a', border: `1px solid ${PANEL_BORDER}`, borderRadius: 6, color: '#94a3b8', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {pickerFacilityFilter === 'all' ? 'All' : pickerFacilityFilter}
                  <ChevronDown size={11} color="#475569" />
                </button>
                {pickerDropOpen && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 50, background: '#0c0f1a', border: `1px solid ${PANEL_BORDER}`, borderRadius: 6, minWidth: 160, overflow: 'hidden' }}>
                    {(['all', ...pickerFacilities]).map((opt) => (
                      <button
                        key={opt}
                        onClick={() => { setPickerFacilityFilter(opt); setPickerDropOpen(false); }}
                        style={{ width: '100%', textAlign: 'left', padding: '8px 12px', background: pickerFacilityFilter === opt ? 'rgba(59,130,246,0.12)' : 'transparent', border: 'none', borderBottom: `1px solid ${PANEL_BORDER}`, color: pickerFacilityFilter === opt ? '#60a5fa' : '#94a3b8', fontSize: 12, cursor: 'pointer' }}
                      >
                        {opt === 'all' ? 'All Facilities' : opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Card grid */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '12px 14px' }}>
              {pickerLoading && (
                <div style={{ padding: 24, textAlign: 'center', color: '#475569', fontSize: 13 }}>Loading…</div>
              )}
              {!pickerLoading && pickerError && (
                <div style={{ padding: 24, textAlign: 'center', color: '#f87171', fontSize: 13 }}>{pickerError}</div>
              )}
              {!pickerLoading && !pickerError && (() => {
                const q = pickerSearch.toLowerCase();
                const filtered = facilityUsers.filter((u) => {
                  if (u.id === uid) return false;
                  if (q && !dName(u).toLowerCase().includes(q) && !u.username.toLowerCase().includes(q)) return false;
                  if (pickerFacilityFilter !== 'all' && u.facility_name !== pickerFacilityFilter) return false;
                  return true;
                });
                if (filtered.length === 0) return (
                  <div style={{ padding: 24, textAlign: 'center', color: '#475569', fontSize: 13 }}>No team members found</div>
                );
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    {filtered.map((u) => {
                      const name = dName(u);
                      const color = getUserColor(u.id);
                      const roleLabel = u.role.replace(/_/g, ' ');
                      const facAbbrev = u.facility_name
                        ? u.facility_name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')
                        : null;
                      return (
                        <button
                          key={u.id}
                          onClick={() => pickUser(u)}
                          style={{ background: '#0f172a', border: `1px solid ${PANEL_BORDER}`, borderRadius: 8, padding: '14px 8px 12px', textAlign: 'center', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, transition: 'border-color 0.15s' }}
                          onMouseEnter={(e) => (e.currentTarget.style.borderColor = color + '88')}
                          onMouseLeave={(e) => (e.currentTarget.style.borderColor = PANEL_BORDER)}
                        >
                          {/* Avatar + online indicator */}
                          <div style={{ position: 'relative' }}>
                            <div style={{ width: 46, height: 46, borderRadius: '50%', background: color + '30', border: `2px solid ${color}66`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color }}>
                              {avatarInitials(name)}
                            </div>
                            {/* Online/offline dot — gray placeholder (no live presence yet) */}
                            <div style={{ position: 'absolute', bottom: 1, right: 1, width: 10, height: 10, borderRadius: '50%', background: '#334155', border: '2px solid #0f172a' }} />
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{name}</div>
                          <div style={{ fontSize: 10, color: '#475569', textTransform: 'capitalize' }}>{roleLabel}</div>
                          {u.department_name && u.department_color && (
                            <span style={deptBadge(u.department_color, u.department_name)}>{u.department_name}</span>
                          )}
                          {facAbbrev && (
                            <div style={{ fontSize: 9, color: '#334155', fontWeight: 600, letterSpacing: '0.04em', background: '#1e293b', borderRadius: 3, padding: '1px 5px' }}>{facAbbrev}</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Delete conversation confirmation ─────────────────────────────────────── */}
      {showDeleteConfirm && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
          <div style={{ background: '#0c0f1a', border: `1px solid ${PANEL_BORDER}`, borderRadius: 10, padding: '20px 24px', width: 280 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 8 }}>Delete conversation?</div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>Delete this conversation? This cannot be undone.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={deleteDmConversation} style={{ flex: 1, padding: '8px 0', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Delete</button>
              <button onClick={() => setShowDeleteConfirm(false)} style={{ flex: 1, padding: '8px 0', background: 'transparent', border: `1px solid ${PANEL_BORDER}`, color: '#94a3b8', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* ── Grant-password modal ──────────────────────────────────────────────────── */}
      {grantModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="card" style={{ width: '100%', maxWidth: 380, padding: 28 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 700 }}>Set New Password</h3>
            <label className="form-label">New Password</label>
            <input className="form-input" type="password" value={grantPw} onChange={(e) => setGrantPw(e.target.value)} placeholder="Min. 8 characters" style={{ marginBottom: 12 }} />
            <label className="form-label">Confirm Password</label>
            <input className="form-input" type="password" value={grantConfirm} onChange={(e) => setGrantConfirm(e.target.value)} placeholder="Repeat password" />
            {grantErr && <p style={{ margin: '8px 0 0', fontSize: 12, color: '#f87171' }}>{grantErr}</p>}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={submitGrant} disabled={grantLoading}>{grantLoading ? 'Saving…' : 'Save Password'}</button>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setGrantModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
